import { useEffect } from 'react';
import { FaxDraftQueries } from '@frontend/api-fax-draft';
import { useTranslation } from '@frontend/i18n';
import { useModalControl, ConfirmationModal, useAlert } from '@frontend/design-system';
import { useLeaveConfirmation } from '../../hooks/use-leave-confirmation/use-leave-confirmation';

type LeaveConfirmationModalProps = {
  draftId?: string;
  shouldBlock: boolean;
  locationId?: string;
  onSaveAndContinue?: () => Promise<void> | void;
  onDeleteDraft?: () => Promise<void> | void;
};

export const LeaveConfirmationModal = ({
  draftId,
  shouldBlock,
  onSaveAndContinue,
  locationId,
  onDeleteDraft,
}: LeaveConfirmationModalProps) => {
  const alert = useAlert();
  const { t } = useTranslation('fax');
  const { modalProps, triggerProps } = useModalControl();

  const invalidateDraft = FaxDraftQueries.useInvalidateDraftList();
  const { mutateAsync: deleteDraft } = FaxDraftQueries.useDeleteDraft();

  const { allowNavigation, blockNavigation } = useLeaveConfirmation({
    allowedRoutePattern: /^\/fax\/drafts\/[0-9a-fA-F-]+(?:\/.*)?$/,
    shouldBlock,
    onBlockHandler: () => {
      triggerProps.onClick();
    },
  });

  useEffect(() => {
    if (shouldBlock) {
      blockNavigation();
    } else {
      allowNavigation();
    }
  }, [shouldBlock]);

  const closeModal = () => {
    modalProps.onClose();
    blockNavigation();
  };

  const handleSaveAndContinue = async () => {
    if (!draftId) {
      alert.error(t('Unable to save draft.'));
      allowNavigation();
      return;
    }
    try {
      await onSaveAndContinue?.();
    } catch (error) {
      alert.error(t('Error saving draft.'));
    } finally {
      allowNavigation();
    }
  };

  const handleDeleteDraft = async () => {
    if (!draftId) {
      alert.error(t('Error deleting draft.'));
      allowNavigation();
      return;
    }
    try {
      if (onDeleteDraft) {
        await onDeleteDraft();
      } else {
        await deleteDraft({ draftId, locationId: locationId ?? '' });
      }
      invalidateDraft();
    } catch (error) {
      alert.error(t('Error deleting draft.'));
    } finally {
      allowNavigation();
    }
    return;
  };

  return (
    <ConfirmationModal
      {...modalProps}
      title={t('Are you sure?')}
      message={t(
        'If you leave now your work may not be saved. To come back to this later select "Save as Draft". Weave will keep your draft for 90 days.'
      )}
      confirmLabel={t('Save As Draft')}
      cancelLabel={t('Delete')}
      backLabel={t('Cancel')}
      onConfirm={handleSaveAndContinue}
      onCancel={handleDeleteDraft}
      onBackClick={closeModal}
      secondaryDestructive={true}
    />
  );
};
